import {
  NavigationDrawer,
  NavigationDrawerContextProvider,
  NavigationDrawerChild,
} from '@risk-first/ui-navigation-drawer';
import { FC, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDataImportAccess } from '../atlas/hooks/use-data-import-access';
import { usePortfolioSetupOldFeature, usePortfoliosFeature, useSplitFlagsFeature } from '../split/hooks';
import { getNavRoutes } from './Navigation.routes';
import { StyledNavigationContent, StyledNavigationDrawerContainer } from './Navigation.style';

export const Navigation: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { pathname, search } = useLocation();
  const isPortfoliosFeatureOn = usePortfoliosFeature();
  const isSplitFlagsFeatureOn = useSplitFlagsFeature();
  const isPortfolioSetupOldFeatureOn = usePortfolioSetupOldFeature();
  const { isAllowed: isDataImportAllowed } = useDataImportAccess();
  const publicRoutes = useMemo(() => {
    const routes = getNavRoutes();

    return routes.reduce<typeof routes>((acc, { children, ...route }) => {
      const activeChildren = children.filter(({ path }) => {
        switch (path) {
          case '/split-flags':
            return isSplitFlagsFeatureOn;
          case '/portfolios':
            return isPortfoliosFeatureOn;
          case '/valuation-runs':
            return isPortfolioSetupOldFeatureOn;
          case '/valuation-range-runs':
            return isPortfolioSetupOldFeatureOn;
          case '/data-import/pipelines':
          case '/data-import/runs':
          case '/data-import/variable-sets':
          case '/data-import/task-types':
            return isDataImportAllowed;
          default:
            return true;
        }
      });

      return activeChildren.length ? [...acc, { ...route, children: activeChildren }] : acc;
    }, []);
  }, [isPortfoliosFeatureOn, isSplitFlagsFeatureOn, isPortfolioSetupOldFeatureOn, isDataImportAllowed]);

  return (
    <NavigationDrawerContextProvider>
      <StyledNavigationDrawerContainer>
        <NavigationDrawer homeLink={<Link aria-label="Home" to={{ pathname: '/' }} />}>
          {publicRoutes.map(({ page, icon, children }) => (
            <NavigationDrawerChild key={page} icon={icon} text={page} uniqueId={`navigation-drawer-parent-${page}`}>
              {children.map((childRoute) => (
                <NavigationDrawerChild
                  key={childRoute.page}
                  aria-label={childRoute.page}
                  icon={childRoute.icon}
                  link={<Link to={{ pathname: childRoute.path, search }}>{childRoute.page}</Link>}
                  selected={pathname === childRoute.path || pathname.startsWith(childRoute.path + '/')}
                  text={childRoute.page}
                  uniqueId={`navigation-drawer-child-${childRoute.page}`}
                />
              ))}
            </NavigationDrawerChild>
          ))}
        </NavigationDrawer>
        <StyledNavigationContent>{children}</StyledNavigationContent>
      </StyledNavigationDrawerContainer>
    </NavigationDrawerContextProvider>
  );
};
